import { useMutation } from "@apollo/client/react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  GetReferenceDetailDocument,
  UpdateReferenceFileDocument,
  type UpdateReferenceFileMutation,
  type UpdateReferenceFileMutationVariables,
} from "~/features/references/gqls";
import type { ReferenceFileEditFormData } from "~/features/references/types";
import { useTenant } from "~/features/tenant/context";
import { translateError } from "~/lib/errors";

/** Edit a reference file's folio offset (`cnutUpdateReferenceFile`). */
export function useReferenceFileEdit({
  referenceId,
  referenceFileId,
  onSuccess,
}: {
  referenceId: string;
  referenceFileId: string;
  onSuccess?: () => void;
}) {
  const { t } = useTranslation();
  const { tenantId } = useTenant();
  const [processing, setProcessing] = useState(false);
  const [updateFile] = useMutation<UpdateReferenceFileMutation, UpdateReferenceFileMutationVariables>(
    UpdateReferenceFileDocument,
  );

  const submit = useCallback(
    async (form: ReferenceFileEditFormData) => {
      if (!tenantId) {
        toast.error(t("error.unknown"));
        return false;
      }
      setProcessing(true);
      try {
        await updateFile({
          refetchQueries: [{ query: GetReferenceDetailDocument, variables: { tenantId, id: referenceId } }],
          variables: { input: { tenantId, id: referenceFileId, folioOffset: Number(form.folioOffset) } },
        });
        toast.success(t("feature.common.updatedSuccessfully", { model: "referenceFile" }));
        onSuccess?.();
        return true;
      } catch (e) {
        toast.error(translateError(t, e));
        return false;
      } finally {
        setProcessing(false);
      }
    },
    [updateFile, tenantId, referenceId, referenceFileId, t, onSuccess],
  );

  return { submit, processing };
}
